'use client';
import React, { useEffect, useRef, useState } from 'react';
import { CircleHelpIcon } from 'lucide-react';
import * as Popover from '@radix-ui/react-popover';

interface InputDescriptionProps {
  description: string;
  onHelpVisible: (visible: boolean) => void;
}
const InputDescription: React.FC<InputDescriptionProps> = ({
  description,
  onHelpVisible,
}) => {
  const [showPopover, setShowPopover] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = (event: MouseEvent): void => {
    if (!popoverRef.current) return;
    if (
      popoverRef.current &&
      !popoverRef.current.contains(event.target as Node)
    ) {
      setShowPopover(false);
    }
  };

  useEffect(() => {
    onHelpVisible(showPopover);
    if (showPopover) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPopover, onHelpVisible]);

  if (!description) {
    return null;
  }

  return (
    <div
      className="!absolute top-auto right-1 group w-4 flex items-center justify-center"
      ref={popoverRef}
    >
      <Popover.Root open={showPopover} onOpenChange={setShowPopover}>
        <Popover.Trigger asChild>
          <button
            className={`${showPopover ? 'visible' : 'invisible'} group-hover:visible w-5 h-5 flex items-center justify-center text-gray-500 cursor-pointer`}
            aria-label="Help information"
            tabIndex={-1}
          >
            <CircleHelpIcon className="w-4 h-4" />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            className="p-2 bg-white shadow-lg border rounded-md text-sm max-w-xs break-words overflow-hidden z-50"
            sideOffset={5}
          >
            <div className="break-words">
              <div dangerouslySetInnerHTML={{ __html: description }} />
            </div>
            <Popover.Arrow className="fill-white" />
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
};

export default InputDescription;
